import { useState, useEffect } from "react";

export const FriendWorkExperiencePage = ({ data }) => {
  const [experiences, setExperiences] = useState([]);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [openWorkDetails, setOpenWorkDetails] = useState(true);
  const [openWorkAddress, setOpenWorkAddress] = useState(true);

  const workDetailsFieldKeys = new Set([
    "company_name",
    "work_current_job",
    "work_experience_pin_code",
    "city_region_country",
    "job_title",
    "work_start_date",
    "work_end_date",
    "job_function",
    "job_sub_functions",
    "job_description",
  ]);

  const workAddressFieldKeys = new Set([
    "floor_and_office_number",
    "building_name",
    "work_address_street_name",
    "work_address_pin_code",
    "work_address_google_map",
    "company_website",
  ]);

  const fieldConfig = [
    { key: "work_current_job", label: "Current Job" },
    { key: "company_name", label: "Company Name" },
    // { key: "work_current_job", label: "Current Job" },
    { key: "work_experience_pin_code", label: "Pincode" },
    { key: "city_region_country", label: "City, Region and Country" },
    { key: "job_title", label: "Job Title" },
    { key: "work_start_date", label: "Start Date", icon: "../input-logos/calander.svg" },
    { key: "work_end_date", label: "End Date", icon: "../input-logos/calander.svg" },
    { key: "job_function", label: "Job Function" },
    { key: "job_sub_functions", label: "Job Sub-Function" },
    { key: "job_description", label: "Description" },
    { key: "company_location", label: "Company Location" },
    { key: "duration_of_work", label: "Duration of Work", icon: "../input-logos/calander.svg" },
    { key: "floor_and_office_number", label: "Building Number/Office Number" },
    { key: "building_name", label: "Building Name" },
    { key: "work_address_street_name", label: "Street Name" },
    { key: "work_address_pin_code", label: "Pincode" },
    { key: "work_address_google_map", label: "Address" },
    { key: "company_website", label: "Website" },
  ];

  // ✅ Load data from parent
  useEffect(() => {
    if (Array.isArray(data) && data.length > 0) {
      setExperiences(data);
    } else {
      setExperiences([]);
    }
  }, [data]);

  // ✅ When card clicked → open full details
  const handleCardClick = (exp) => {
    setSelectedExperience(exp);
    setOpenWorkDetails(true);
    setOpenWorkAddress(true);
  };

  // ✅ Back button from details
  const handleBack = () => {
    setSelectedExperience(null);
  };

  // ✅ Toggle current-work locally (UI-only)
  const toggleCurrentWork = (targetExp) => {
    setExperiences((prev) =>
      prev.map((e) => {
        const isSame = targetExp.experience_id ? e.experience_id === targetExp.experience_id : e === targetExp;
        if (!isSame) return e;
        const newVal = String(Number(e?.work_current_job) === 1 ? 0 : 1);
        return { ...e, work_current_job: newVal };
      })
    );

    if (selectedExperience) {
      const isSameSelected = targetExp.experience_id
        ? selectedExperience.experience_id === targetExp.experience_id
        : selectedExperience === targetExp;
      if (isSameSelected) {
        setSelectedExperience((prev) => ({ ...prev, work_current_job: String(Number(prev?.work_current_job) === 1 ? 0 : 1) }));
      }
    }
  };

  // ✅ Get initials for user avatars
  // getInitials removed (unused)

  const getFieldValueByKey = (exp, key) => {
    const value = exp?.[key];
    if (value === undefined || value === null) return "";
    return String(value).trim() !== "" ? value : "";
  };

  const isPlusCodePart = (value = "") =>
    /^[A-Z0-9]{4,}\+[A-Z0-9]{2,}/i.test(String(value || "").trim());

  const cleanMapDetail = (value = "") => {
    const text = String(value || "").trim();
    return isPlusCodePart(text) ? "" : text;
  };

  const getPreviewIcon = (key) => {
    if (["work_start_date", "work_end_date", "duration_of_work", "work_current_job"].includes(key)) return "../input-logos/calander.svg";
    if (["work_experience_pin_code", "work_address_pin_code", "city_region_country", "company_location", "work_address_google_map"].includes(key)) return "../input-logos/location.svg";
    if (["floor_and_office_number", "building_name", "work_address_street_name"].includes(key)) return "../input-logos/building.svg";
    return "../input-logos/user.svg";
  };

  const renderPreviewFieldCard = (field) => {
    const value = String(field.value || "").trim();
    if (!value) return null;

    return (
      <div
        key={field.key}
        className={`${field.key === "work_address_google_map" ? "min-[1100px]:col-span-2" : ""} flex items-center gap-3 px-4 py-4 border-b last:border-b-0`}
      >
        <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-50 text-[#6938EF]">
          <img src={field.icon || getPreviewIcon(field.key)} alt="" className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="text-xs text-gray-500">{field.label}</div>
          <div className="text-sm font-medium text-gray-900 break-words">{value || "—"}</div>
        </div>
      </div>
    );
  };

  const renderCurrentStatusPreview = (label) => (
    <div className="min-[1100px]:col-span-2 px-4 py-4 border-b last:border-b-0">
      <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
        <span className="text-sm font-semibold text-gray-900">{label}</span>
        <span className="relative inline-flex h-6 w-11 items-center rounded-full bg-[#6938EF]">
          <span className="absolute right-1 h-4 w-4 rounded-full bg-white shadow-sm" />
        </span>
      </div>
    </div>
  );

  const formatWorkAddress = (exp) => {
    const addressParts = [
      cleanMapDetail(exp?.building_name),
      cleanMapDetail(exp?.floor_and_office_number),
      cleanMapDetail(exp?.work_address_street_name),
      exp?.work_address_area_locality,
      exp?.work_address_city,
      exp?.work_address_region,
      exp?.work_address_country,
      exp?.work_address_pin_code || exp?.work_experience_pin_code,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    if (addressParts.length > 0) {
      return addressParts.join(", ");
    }

    return exp?.city_region_country || exp?.work_address_google_map || "";
  };

  const formatWorkDuration = (exp) => {
    const parseMonth = (value) => {
      const text = String(value || "").trim();
      if (!text) return null;

      let year;
      let month;
      if (text.includes("-")) {
        const [y, m] = text.split("-");
        year = Number(y);
        month = Number(m);
      } else if (text.includes("/")) {
        const [m, y] = text.split("/");
        year = Number(y);
        month = Number(m);
      } else {
        return null;
      }

      if (!Number.isNaN(year) && !Number.isNaN(month)) {
        return { year, month };
      }
      return null;
    };

    const monthNamesAbbrev = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const start = parseMonth(exp?.work_start_date);
    const end = parseMonth(exp?.work_end_date);

    if (!start && !end) return "";

    const fmt = (d) => {
      if (!d) return "";
      const m = Number(d.month);
      const y = Number(d.year);
      if (Number.isNaN(m) || Number.isNaN(y)) return "";
      const name = monthNamesAbbrev[(m - 1) % 12] || "";
      return `${name} ${y}`;
    };

    const startText = fmt(start);
    const endText = end ? fmt(end) : (Number(exp?.work_current_job) === 1 ? "Present" : "");

    if (startText && endText) return `${startText} - ${endText}`;
    if (startText && !endText) return startText + (Number(exp?.work_current_job) === 1 ? " - Present" : "");
    if (!startText && endText) return endText;
    return "";
  };

  const getDetailsFields = (exp) => {
    return fieldConfig
      .map((field) => ({
        ...field,
        value:
          field.key === "work_current_job"
            ? (Number(exp?.work_current_job) === 1 ? "Yes" : "No")
            : field.key === "company_location"
              ? formatWorkAddress(exp)
              : field.key === "duration_of_work"
                ? formatWorkDuration(exp) || (Number(exp?.work_current_job) === 1 ? "Present" : "")
                : field.key === "work_address_pin_code"
                  ? getFieldValueByKey(exp, "work_address_pin_code") || getFieldValueByKey(exp, "work_experience_pin_code")
                : getFieldValueByKey(exp, field.key),
      }))
      .filter((field) => String(field.value || "").trim() !== "");
  };

  const getSummaryValues = (exp) => {
    return {
      companyName: getFieldValueByKey(exp, "company_name"),
      companyLocation: formatWorkAddress(exp),
      jobTitle: getFieldValueByKey(exp, "job_title"),
      durationOfWork: formatWorkDuration(exp) || (Number(exp?.work_current_job) === 1 ? "Present" : ""),
      jobFunction: getFieldValueByKey(exp, "job_function"),
    };
  };

  const hasRenderableWorkExperience = (exp) => {
    const summary = getSummaryValues(exp);
    return Boolean(
      String(summary.companyName || "").trim() ||
      String(summary.companyLocation || "").trim() ||
      String(summary.jobTitle || "").trim() ||
      String(summary.durationOfWork || "").trim()
    ) && !(
      !String(summary.companyName || "").trim() &&
      !String(summary.companyLocation || "").trim() &&
      !String(summary.jobTitle || "").trim() &&
      String(summary.durationOfWork || "").trim() === "Present"
    );
  };

  const selectedDetails = selectedExperience ? getDetailsFields(selectedExperience) : [];
  const hasMeaningfulWorkDetails = selectedDetails.some((field) => field.key !== "work_current_job");
  const selectedWorkDetails = selectedDetails.filter((field) => workDetailsFieldKeys.has(field.key));
  const selectedWorkAddress = selectedDetails.filter((field) => workAddressFieldKeys.has(field.key));
  const displayExperiences = experiences.filter(hasRenderableWorkExperience);

  return (
    <>
      {selectedExperience ? (
        // 🔹 Full Detail View
        <div>
          <div className="rounded-2xl p-4 min-[900px]:p-6 border border-gray-100 shadow-sm">
            <div className="flex justify-start items-center">
              <button
                aria-label={`Back to previous page`}
                onClick={handleBack}
                className="flex items-center aoboshi-one text-lg text-[#212121] font-medium p-4 rounded-2xl"
              >
                <img src="../back-arrow.svg" alt="back-arrow" className="mr-4" />
              </button>
              <h2 className="text-lg font-semibold text-gray-900">
                Work Experience Details
              </h2>
            </div>

            <div className="min-h-0  [&::-webkit-scrollbar]:hidden">
              <div className="mt-4 gap-4">
                {hasMeaningfulWorkDetails && selectedWorkDetails.length > 0 && (
                  <div className="mb-4 border border-gray-100 rounded-xl overflow-hidden bg-white">
                    <button
                      type="button"
                      onClick={() => setOpenWorkDetails((prev) => !prev)}
                      className={`w-full flex items-center justify-between px-5 py-4 transition-all ${openWorkDetails
                          ? "bg-gradient-to-r from-[#6938EF]/5 to-transparent border-b border-[#E6E2FF]"
                          : "bg-gradient-to-r from-gray-50 to-white"
                        }`}
                    >
                      <span className={`font-semibold transition-colors ${openWorkDetails ? "text-[#6938EF]" : "text-gray-900"}`}>
                        Work Experience
                      </span>
                      <span className={`transition-transform duration-300 ${openWorkDetails ? "rotate-180 text-[#6938EF]" : "text-gray-600"}`}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </button>

                    {openWorkDetails && (
                      <div className="grid grid-cols-1 min-[1100px]:grid-cols-2 gap-0 px-4 pb-4">
                        {selectedWorkDetails.map((field) => {
                          if (field.key === "work_current_job") {
                            // only render the Current Job row when the value indicates current (1)
                            if (Number(selectedExperience?.work_current_job) !== 1) return null;
                            return renderCurrentStatusPreview("Current Job");
                          }
                          return renderPreviewFieldCard(field);
                        })}
                      </div>
                    )}
                  </div>
                )}

                {selectedWorkAddress.length > 0 && (
                  <div className="mb-4 border border-gray-100 rounded-xl overflow-hidden bg-white">
                    <button
                      type="button"
                      onClick={() => setOpenWorkAddress((prev) => !prev)}
                      className={`w-full flex items-center justify-between px-5 py-4 transition-all ${openWorkAddress
                          ? "bg-gradient-to-r from-[#6938EF]/5 to-transparent border-b border-[#E6E2FF]"
                          : "bg-gradient-to-r from-gray-50 to-white"
                        }`}
                    >
                      <span className={`font-semibold transition-colors ${openWorkAddress ? "text-[#6938EF]" : "text-gray-900"}`}>
                        Work Address
                      </span>
                      <span className={`transition-transform duration-300 ${openWorkAddress ? "rotate-180 text-[#6938EF]" : "text-gray-600"}`}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    </button>

                    {openWorkAddress && (
                      <div className="grid grid-cols-1 min-[1100px]:grid-cols-2 gap-0 px-4 pb-4">
                        {selectedWorkAddress.map(renderPreviewFieldCard)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        // 🔹 Card List View (Same Design)
        <div className="rounded-2xl p-4 min-[900px]:p-6 border border-gray-100 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Work Experience
            </h2>
          </div>

          <div className="space-y-6 min-h-0 py-5">
            {displayExperiences.length > 0 ? (
              displayExperiences.map((exp, index) => (
                (() => {
                  const summary = getSummaryValues(exp);
                  return (
                    <div
                      key={exp.experience_id || index}
                      onClick={() => handleCardClick(exp)}
                      className="relative rounded-2xl border-gray-200 p-8 bg-white transition"
                    >
                      {/* card-level toggle removed (kept minimal) */}

                      {/* 🔹 Redesigned Card Content (education-style) */}
                      <div className="space-y-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-4 min-w-0">
                            <div className="flex-shrink-0">
                              <div className="h-12 w-12 p-2 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] text-white">
                               <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#fff"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M4 12H3V8C3 6.89543 3.89543 6 5 6H9M4 12V18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V12M4 12H10M20 12H21V8C21 6.89543 20.1046 6 19 6H15M20 12H14M14 12V10H10V12M14 12V14H10V12M9 6V5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V6M9 6H15" stroke="#fff" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                              </div>
                            </div>

                            <div className="min-w-0">
                              <h3 className="text-lg font-bold text-gray-900 truncate">{summary.companyName || "—"}</h3>
                              <p className="mt-1 text-sm text-gray-600 truncate flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                <span>{summary.companyLocation || "—"}</span>
                              </p>
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-2">
                            {exp.work_current_job === "1" && (
                              <div className=" h-5 w-5 sm:h-6 sm:w-6 top-2 sm:top-3 left-2 sm:left-3 flex justify-center items-center bg-[#6938EF] rounded-[8px] sm:rounded-[12px] p-1 shadow-sm">
                                <svg fill="#ffffff" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" stroke="#ffffff">
                                  <g id="SVGRepo_iconCarrier">
                                    <path d="M16.948 20.2a1 1 0 0 1-.465-.115l-4.472-2.352-4.471 2.348a1 1 0 0 1-1.451-1.054l.854-4.979-3.619-3.527a1 1 0 0 1 .554-1.706l5-.727 2.237-4.531A1 1 0 0 1 12.011 3a1 1 0 0 1 .9.558l2.236 4.53 5 .727a1 1 0 0 1 .554 1.706l-3.621 3.526.854 4.98a1 1 0 0 1-.986 1.173zM6.171 10.5l2.544 2.479a1 1 0 0 1 .285.888l-.6 3.5 3.144-1.653a.993.993 0 0 1 .931 0l3.144 1.653-.6-3.5a1 1 0 0 1 .288-.885l2.544-2.482-3.515-.511a1 1 0 0 1-.752-.547l-1.573-3.183-1.573 3.186a1 1 0 0 1-.752.547z" />
                                  </g>
                                </svg>
                              </div>
                            )}
                            {/* <button type="button" className="p-1 rounded-full text-gray-400 hover:text-gray-600">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="5" r="1.5" fill="currentColor" /><circle cx="12" cy="12" r="1.5" fill="currentColor" /><circle cx="12" cy="19" r="1.5" fill="currentColor" /></svg>
                            </button> */}
                          </div>
                        </div>

                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-gray-700">
                          <div className="flex flex-col">
                            <span className="text-xs text-gray-500">Period</span>
                            <span className="font-medium mt-1 text-gray-900">{summary.durationOfWork || "—"}</span>
                          </div>

                          <div className="flex flex-col">
                            <span className="text-xs text-gray-500">Role</span>
                            <span className="font-medium mt-1 text-gray-900">{summary.jobTitle || "—"}</span>
                          </div>

                          <div className="flex flex-col">
                            <span className="text-xs text-gray-500">Job Function</span>
                            <span className="font-medium mt-1 text-gray-900">{summary.jobFunction || "—"}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()
              ))
            ) : (
              <div className="flex flex-col h-[70dvh] justify-center items-center">
                <img src="../no-data/no-work.svg" alt="No data" className="mx-auto mb-4 h-40 w-40" />
                <p className="font-semibold text-[28px] text-center">No work experience added yet.</p>
                <p className="text-gray-500 text-center">This user hasn’t added work experience yet.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
